import { useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Upload, X, FileSpreadsheet, AlertTriangle, Check, ChevronDown } from 'lucide-react'
import { parseCsv, readFileAsText } from '../../services/csvImport'

export interface ImportField {
  key: string
  label: string
  required?: boolean
  options?: { value: string; label: string }[]
}

interface CsvImportDialogProps {
  isOpen: boolean
  onClose: () => void
  title: string
  fields: ImportField[]
  onImport: (rows: Record<string, string>[]) => Promise<{ imported: number; errors: string[] }>
}

export function CsvImportDialog({ isOpen, onClose, title, fields, onImport }: CsvImportDialogProps) {
  const [step, setStep] = useState<'upload' | 'map' | 'preview' | 'result'>('upload')
  const [csvHeaders, setCsvHeaders] = useState<string[]>([])
  const [csvRows, setCsvRows] = useState<string[][]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<{ imported: number; errors: string[] } | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  const reset = useCallback(() => {
    setStep('upload')
    setCsvHeaders([])
    setCsvRows([])
    setMapping({})
    setImporting(false)
    setResult(null)
    setFileError(null)
  }, [])

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleFile = async (file: File) => {
    setFileError(null)
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (ext !== 'csv' && ext !== 'tsv' && ext !== 'txt') {
      setFileError('Please upload a CSV file. Excel (.xlsx) files should be exported as CSV first.')
      return
    }

    try {
      const text = await readFileAsText(file)
      const { headers, rows } = parseCsv(text)
      if (headers.length === 0 || rows.length === 0) {
        setFileError('File appears empty or has no data rows.')
        return
      }

      setCsvHeaders(headers)
      setCsvRows(rows)

      // Auto-map columns by fuzzy matching field labels/keys to CSV headers
      const autoMap: Record<string, string> = {}
      for (const field of fields) {
        const match = headers.find(h => {
          const hLower = h.toLowerCase().replace(/[^a-z0-9]/g, '')
          const fLabel = field.label.toLowerCase().replace(/[^a-z0-9]/g, '')
          const fKey = field.key.toLowerCase().replace(/[^a-z0-9]/g, '')
          return hLower === fLabel || hLower === fKey || hLower.includes(fLabel) || fLabel.includes(hLower)
        })
        if (match) autoMap[field.key] = match
      }
      setMapping(autoMap)
      setStep('map')
    } catch {
      setFileError('Failed to read file.')
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }

  // Mapped preview rows
  const previewRows = useMemo(() => {
    return csvRows.slice(0, 10).map(row => {
      const obj: Record<string, string> = {}
      for (const field of fields) {
        const csvCol = mapping[field.key]
        if (csvCol) {
          const colIdx = csvHeaders.indexOf(csvCol)
          obj[field.key] = colIdx >= 0 ? (row[colIdx] || '') : ''
        } else {
          obj[field.key] = ''
        }
      }
      return obj
    })
  }, [csvRows, csvHeaders, mapping, fields])

  const requiredMissing = fields.filter(f => f.required && !mapping[f.key])

  const handleImport = async () => {
    setImporting(true)
    try {
      // Build all rows
      const allRows = csvRows.map(row => {
        const obj: Record<string, string> = {}
        for (const field of fields) {
          const csvCol = mapping[field.key]
          if (csvCol) {
            const colIdx = csvHeaders.indexOf(csvCol)
            obj[field.key] = colIdx >= 0 ? (row[colIdx] || '') : ''
          }
        }
        return obj
      })

      const res = await onImport(allRows)
      setResult(res)
      setStep('result')
    } catch (err) {
      setResult({ imported: 0, errors: [err instanceof Error ? err.message : 'Import failed'] })
      setStep('result')
    } finally {
      setImporting(false)
    }
  }

  if (!isOpen) return null

  return createPortal(
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal modal-scrollable" onClick={e => e.stopPropagation()} style={{ maxWidth: 800, width: '95vw' }}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={handleClose}><X size={20} /></button>
        </div>

        <div className="modal-body">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div>
              <div
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}
                style={{
                  border: '2px dashed var(--border-light)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '48px 24px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                }}
                onClick={() => document.getElementById('csv-import-input')?.click()}
              >
                <FileSpreadsheet size={48} style={{ color: 'var(--accent-blue)', marginBottom: 16, opacity: 0.7 }} />
                <p style={{ margin: '0 0 8px', fontSize: 'var(--font-size-lg)', fontWeight: 500 }}>
                  Drop a CSV file here or click to browse
                </p>
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
                  Supports .csv files. Export from Excel as CSV if needed.
                </p>
                <input
                  id="csv-import-input"
                  type="file"
                  accept=".csv,.tsv,.txt"
                  onChange={handleInput}
                  style={{ display: 'none' }}
                />
              </div>
              {fileError && (
                <div className="info-card danger mt-3" style={{ padding: '12px 16px' }}>
                  <div className="d-flex align-center gap-2">
                    <AlertTriangle size={16} />
                    <span>{fileError}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Column mapping */}
          {step === 'map' && (
            <div>
              <p className="text-secondary-color mb-3" style={{ fontSize: 'var(--font-size-sm)' }}>
                Found <strong>{csvRows.length}</strong> rows with <strong>{csvHeaders.length}</strong> columns.
                Map your CSV columns to the fields below.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '8px 12px', alignItems: 'center' }}>
                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textTransform: 'uppercase' }}>App Field</div>
                <div></div>
                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textTransform: 'uppercase' }}>CSV Column</div>

                {fields.map(field => (
                  <div key={field.key} style={{ display: 'contents' }}>
                    <div style={{ fontSize: 'var(--font-size-sm)' }}>
                      {field.label}
                      {field.required && <span style={{ color: 'var(--accent-red)', marginLeft: 4 }}>*</span>}
                    </div>
                    <ChevronDown size={14} style={{ color: 'var(--text-muted)', transform: 'rotate(-90deg)' }} />
                    <select
                      className="form-select form-select-sm"
                      value={mapping[field.key] || ''}
                      onChange={e => setMapping(prev => ({ ...prev, [field.key]: e.target.value }))}
                    >
                      <option value="">— Skip —</option>
                      {csvHeaders.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {requiredMissing.length > 0 && (
                <div className="info-card danger mt-3" style={{ padding: '10px 14px', fontSize: 'var(--font-size-sm)' }}>
                  <AlertTriangle size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                  Required fields not mapped: {requiredMissing.map(f => f.label).join(', ')}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && (
            <div>
              <p className="text-secondary-color mb-3" style={{ fontSize: 'var(--font-size-sm)' }}>
                Preview of first {Math.min(10, csvRows.length)} rows (of {csvRows.length} total). Confirm and import.
              </p>
              <div className="table-container" style={{ maxHeight: 400, overflowY: 'auto' }}>
                <table className="data-table data-table-compact">
                  <thead>
                    <tr>
                      <th>#</th>
                      {fields.filter(f => mapping[f.key]).map(f => (
                        <th key={f.key}>{f.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        {fields.filter(f => mapping[f.key]).map(f => (
                          <td key={f.key} style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row[f.key] || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Step 4: Result */}
          {step === 'result' && result && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              {result.imported > 0 ? (
                <>
                  <div style={{
                    width: 64, height: 64, borderRadius: '50%', background: 'var(--accent-green)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
                  }}>
                    <Check size={32} color="white" />
                  </div>
                  <h3 style={{ margin: '0 0 8px' }}>Import Complete</h3>
                  <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                    Successfully imported <strong>{result.imported}</strong> records.
                  </p>
                </>
              ) : (
                <>
                  <AlertTriangle size={48} style={{ color: 'var(--accent-red)', marginBottom: 16 }} />
                  <h3 style={{ margin: '0 0 8px' }}>Import Failed</h3>
                </>
              )}
              {result.errors.length > 0 && (
                <div className="info-card danger mt-3" style={{ textAlign: 'left', padding: '12px 16px', fontSize: 'var(--font-size-sm)' }}>
                  {result.errors.map((err, i) => <div key={i}>{err}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step === 'upload' && (
            <button className="btn btn-secondary" onClick={handleClose}>Cancel</button>
          )}
          {step === 'map' && (
            <>
              <button className="btn btn-secondary" onClick={() => setStep('upload')}>Back</button>
              <button
                className="btn btn-primary"
                disabled={requiredMissing.length > 0}
                onClick={() => setStep('preview')}
              >
                Preview ({csvRows.length} rows)
              </button>
            </>
          )}
          {step === 'preview' && (
            <>
              <button className="btn btn-secondary" onClick={() => setStep('map')}>Back</button>
              <button className="btn btn-primary" disabled={importing} onClick={handleImport}>
                {importing ? (
                  <><Upload size={16} className="spin" /> Importing...</>
                ) : (
                  <><Upload size={16} /> Import {csvRows.length} Records</>
                )}
              </button>
            </>
          )}
          {step === 'result' && (
            <button className="btn btn-primary" onClick={handleClose}>Done</button>
          )}
        </div>
      </div>
    </div>, document.body
  )
}
